import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { useAppDispatch, useAppSelector } from 'store'
import { GridFilterActionState, initialState, setGridFilters } from 'store/grid'
import { fetchFilterAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { getVersionFilterFields } from './Versions/versionFields'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
  }
}

interface SelectVersionProps {
  onChange: (version: NameDto<string>) => void
  initialValue?: string
}

export const SelectVersion = (props: SelectVersionProps): JSX.Element => {
  const { onChange, initialValue: activeVersion } = props
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState(initialState())
  )

  const [versions, setVersions] = useState<NameDto<string>[]>([])
  const { filters } = useMapStateToProps()
  const { fetchFilters } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(filters)) fetchFilters()
  }, [])

  useEffect(() => {
    fetchVersions()
  }, [
    gridFilterState.pageSize,
    gridFilterState.pageIndex,
    gridFilterState.take,
    tableData?.skipCount,
    gridFilterState.sort?.length,
  ])

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      if (versions === undefined) return null
      if (filter) return buildQuery(filter)
      if (gridFilterState.filter) return buildQuery(gridFilterState.filter)
      return null
    },
    [gridFilterState.filter]
  )

  const fetchVersions = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const search = params?.query !== undefined ? params.query : getQuery()
      const pageIndex = gridFilterState.pageIndex ?? 1
      const pageSize = gridFilterState.pageSize ?? 10
      const take = gridFilterState.take ?? 10
      const skip = gridFilterState.skip ?? 0
      const skipCount = tableData?.skipCount !== false

      const response = await clientAreaApiClient.getVersions({
        search,
        pageIndex,
        pageSize,
        skipCount,
      })

      if (response?.items) setVersions(response.items)
      else setVersions([])
      const total = response.totalResults

      setGridFilterState({ ...gridFilterState, pageIndex, pageSize, skip, take, total })
      setTableData({ ...tableData, meta: response })
    },
    [gridFilterState, tableData]
  )

  const handleSkipCountChange = async (skipCount: boolean) =>
    setTableData({ ...tableData, skipCount })

  const handlePageSizeChange = async (pageSize: number) =>
    setGridFilterState({ ...gridFilterState, pageIndex: 1, pageSize, take: pageSize })

  const handleGridPageChange = async (pageIndex: number) =>
    setGridFilterState({ ...gridFilterState, pageIndex })

  return (
    <div className='select-version' style={{ margin: 16 }}>
      <SelectFiltersFacade
        setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
        filterFetch={fetchVersions}
        filterType={FilterType.Versions}
        gridState={gridFilterState}
        filterFields={getVersionFilterFields()}
        showFilterWhenInactive
      />

      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridFilterState}
          resizable
          data={versions}
          total={gridFilterState.total}
          take={gridFilterState.take}
          skip={gridFilterState.skip}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridFilterState.pageIndex || 1}
              pageSize={gridFilterState.pageSize || 10}
              take={gridFilterState.take || 10}
              skip={gridFilterState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          <GridColumn
            field='Select'
            width={180}
            cell={(gridCellProps: GridCellProps) => (
              <td data-test='select-column'>
                <Button onClick={() => onChange(gridCellProps.dataItem)} data-test='select-button'>
                  {activeVersion === gridCellProps.dataItem.id ? 'Current Version' : 'Select'}
                </Button>
              </td>
            )}
          />
          <GridColumn title='Version' field='name' />
        </Grid>
      </TableStateContext.Provider>
    </div>
  )
}
